import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAutocomplete from "components/MDAutocomplete";
import api from "services/api";

const STAFF_ROLES = [
  { value: "ADMIN", label: "Admin" },
  { value: "PETUGAS", label: "Petugas" },
  { value: "VERIFIKATOR", label: "Verifikator" },
  { value: "PIMPINAN", label: "Pimpinan" },
];

const emptyForm = {
  nik: "",
  nama: "",
  email: "",
  no_hp: "",
  username: "",
  password: "",
  role: "PETUGAS",
  instansi: "DPMPTSP Kabupaten Rote Ndao",
  status: "AKTIF",
};

const STATUS_OPTIONS = ["AKTIF", "NONAKTIF"];

function UserFormDialog({ open, mode, user, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && user) {
      setForm({
        nik: user.nik || "",
        nama: user.nama || "",
        email: user.email || "",
        no_hp: user.no_hp || "",
        username: user.username || "",
        password: "",
        role: user.role || "PETUGAS",
        instansi: user.instansi || "",
        status: user.status || "AKTIF",
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, mode, user]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSelectChange = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nik.trim() || !form.nama.trim() || !form.email.trim() || !form.username.trim()) {
      alert("NIK, nama, email, dan username wajib diisi");
      return;
    }
    if (mode === "create" && !form.password.trim()) {
      alert("Password wajib diisi untuk user baru");
      return;
    }

    const payload = { ...form };
    if (mode === "edit" && !payload.password) {
      delete payload.password;
    }

    setSaving(true);
    try {
      if (mode === "edit" && user?.id) {
        await api.put(`users/${user.id}`, payload);
      } else {
        await api.post("users", payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || "Gagal menyimpan user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {mode === "edit" ? "Edit User Staff" : "Tambah User Staff Baru"}
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MDBox component="form" id="user-staff-form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <MDInput label="NIK (16 digit) *" fullWidth value={form.nik} onChange={handleChange("nik")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput label="Username *" fullWidth value={form.username} onChange={handleChange("username")} />
            </Grid>
            <Grid item xs={12}>
              <MDInput label="Nama Lengkap *" fullWidth value={form.nama} onChange={handleChange("nama")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput label="Email *" type="email" fullWidth value={form.email} onChange={handleChange("email")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput label="No. HP" fullWidth value={form.no_hp} onChange={handleChange("no_hp")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDAutocomplete
                label="Role *"
                fullWidth
                options={STAFF_ROLES}
                value={form.role}
                onChange={handleSelectChange("role")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDAutocomplete
                label="Status"
                fullWidth
                options={STATUS_OPTIONS}
                value={form.status}
                onChange={handleSelectChange("status")}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput label="Instansi" fullWidth value={form.instansi} onChange={handleChange("instansi")} />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label={mode === "edit" ? "Password Baru (kosongkan jika tidak diubah)" : "Password *"}
                type="password"
                fullWidth
                value={form.password}
                onChange={handleChange("password")}
              />
            </Grid>
          </Grid>
          <MDTypography variant="caption" color="text" display="block" mt={2}>
            Hanya Super Admin yang dapat membuat akun Admin, Petugas, Verifikator, dan Pimpinan.
          </MDTypography>
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>
          Batal
        </MDButton>
        <MDButton variant="gradient" color="info" type="submit" form="user-staff-form" disabled={saving}>
          {saving ? "Menyimpan..." : "Simpan"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

UserFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default UserFormDialog;
